import { AddressItem } from "./GeocodeService";
import { RouteResponse } from "../stores/RouteStore"
import { RouteSettings } from "../stores/RouteSettingsStore";
import { ApiError, DataField, RouteService, SingleRouteResponseOfRoadFeatures, SingleRouteResponseOfRoadFeaturesWithBruksklasse } from "./RuteplanAPI";
import { UpdateURL } from "./UrlService";
import { EnvironmentName, IEnvironmentSetting } from "../stores/EnvironmenSettingStore";

export type SingleRouteResponse = SingleRouteResponseOfRoadFeatures | SingleRouteResponseOfRoadFeaturesWithBruksklasse;

function createStopsParam(fromAddress: AddressItem, toAddress:AddressItem, viaPoints: Array<AddressItem>) {
    if (viaPoints.length === 0) {
        return fromAddress.location+";"+toAddress.location;        
    }

    let viaPointString = ";";
    for (let viaPoint of viaPoints) {
        viaPointString += viaPoint.location+";";
    }    
    
    return fromAddress.location+viaPointString+toAddress.location;    
}

function createBarriers(barriers: Array<AddressItem>) {

    if (barriers.length === 0) {
        return null;
    }
    
    let result = "";
    for (let i = 0; i < barriers.length; i++) {
        result += barriers[i].location;
        if (i < barriers.length - 1) {
            result += ";"
        }
    }

    return result;
}

function routeBest(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBest({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),                
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        includeObstructedRoutes: settings.includeObstructedRoutes
      });
}

function routeBruksklasseNormalTransport(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasseNormalTransport({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        bruksklasse: settings.bruksklasseNormalTransport!,
        includeObstructedRoutes: settings.includeObstructedRoutes
    })
}

function routeBruksklasseSpesialTransport(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasseSpesialTransport({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        bruksklasse: settings.bruksklasseSpesialTransport!,
        veggruppe: settings.veggruppe,
        includeVeggruppeIkke: settings.includeVeggruppeIkke,
        includeObstructedRoutes: settings.includeObstructedRoutes
    })
}

function routeBruksklasseTommerTransport(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasseTommerTransport({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        bruksklasse: settings.bruksklasseTommerTransport!,
        includeObstructedRoutes: settings.includeObstructedRoutes
    })
}

function routeBruksklasseModulvogntog(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasseModulvognTog({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,        
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        bruksklasse: settings.bruksklasseModulvogntog!,
        isLinkSemitrailer: settings.erLinkSemiTrailer,
        medSporingskrav: settings.medSporingskrav,
        includeObstructedRoutes: settings.includeObstructedRoutes   
    });
}

function routeBruksklasse12100(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints:Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasse12100({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,        
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,        
        barriers: createBarriers(barriers),
        includeObstructedRoutes: settings.includeObstructedRoutes                
    });
}

function routeBruksklasse1265(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints:Array<AddressItem>, barriers: Array<AddressItem>): Promise<RouteResponse> {
    return RouteService.routeBruksklasse1265({
        startTime: settings.startTime.format("YYYYMMDDHHmmss"),
        inputSrs: "EPSG_4326",
        outputSrs: settings.outputSrs,        
        stops: createStopsParam(fromAddress, toAddress, viaPoints), 
        returnFields: ["Geometry",DataField.NVDB_REFERENCES],
        avoidSituationIds: settings.avoidSituationIds,
        avoidFeatureInstanceIds: settings.avoidFeatureInstanceIds,
        avoidTrafficMessageTypes: settings.avoidTrafficMessageTypes,
        avoidRoadFeatureTypes: settings.avoidRoadFeatureTypes,
        length: settings.length,
        weight: settings.weight,
        height: settings.height,
        barriers: createBarriers(barriers),
        includeObstructedRoutes: settings.includeObstructedRoutes                
    });
}

export function routeApiErrorMessage(error:any) {
        
    let message = error.message;

    if (error.status === 404) {

        try {
            message = "404: " + JSON.parse(error.body).Message;            
        }
        catch {
            message = "404: " + error.body.message;
        }
      
    }
  
    return message;
}

export async function GetRoute(settings: RouteSettings, fromAddress: AddressItem, toAddress: AddressItem, viaPoints: Array<AddressItem>, barriers: Array<AddressItem>, environmentName: EnvironmentName): Promise<RouteResponse> {   
    UpdateURL(settings, fromAddress, toAddress, viaPoints, barriers, environmentName);
    switch (settings.routeType) {
        case "best":
           return routeBest(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasseNormalTransport":
            return routeBruksklasseNormalTransport(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasseSpesialTransport":
            return routeBruksklasseSpesialTransport(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasseTommerTransport":
            return routeBruksklasseTommerTransport(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasseModulvognTog":
            return routeBruksklasseModulvogntog(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasse12100":
            return routeBruksklasse12100(settings, fromAddress, toAddress, viaPoints, barriers);
        case "bruksklasse1265":
            return routeBruksklasse1265(settings, fromAddress, toAddress, viaPoints, barriers);
        default:
            return routeBest(settings, fromAddress, toAddress, viaPoints, barriers);
    }
}


